import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import ProjectTypePicker, { ProjectTypePills } from '../components/ProjectTypePicker';
import { SortableTh } from '../components/SortableTh';
import { useAuth } from '../context/AuthContext';
import { fetchProjectsCategorized, createProject, type ProjectRow } from '../api/projects';
import { formatTaskDate } from '../utils/formatTaskDate';
import { useTableSortState } from '../hooks/useTableSort';
import { sortRows } from '../utils/tableSort';
import './Page.css';
import './TasksBoard.css';
import './Projects.css';

function ProjectsTableSection({
  title,
  sectionId,
  projects,
  onRowClick,
  emptyText = 'No projects yet.',
}: {
  title: string;
  sectionId: string;
  projects: ProjectRow[];
  onRowClick: (id: number) => void;
  emptyText?: string;
}) {
  const headingId = `projects-section-${sectionId}`;
  const { sortKey, sortDir, toggleSort } = useTableSortState();
  const sorted = useMemo(
    () =>
      sortRows(projects, sortKey, sortDir, (p, key) => {
        switch (key) {
          case 'name':
            return p.name ?? '';
          case 'types':
            return (p.project_types ?? []).join(', ');
          case 'start_date':
            return p.start_date ?? '';
          case 'expected_end_date':
            return p.expected_end_date ?? '';
          case 'owner':
            return p.created_by_username ?? '';
          default:
            return '';
        }
      }),
    [projects, sortKey, sortDir]
  );
  return (
    <section className="projects-table-section" aria-labelledby={headingId}>
      <h2 className="projects-section-title" id={headingId}>
        {title}
      </h2>
      <div className="projects-table-wrap">
        <table className="tasks-search-results-table">
          <thead>
            <tr>
              <SortableTh label="Project name" columnKey="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Types" columnKey="types" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Start date" columnKey="start_date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Expected end" columnKey="expected_end_date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Owner" columnKey="owner" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={5} className="projects-empty-cell">
                  {emptyText}
                </td>
              </tr>
            ) : (
              sorted.map((p) => (
                <tr
                  key={p.id}
                  className="projects-table-row-clickable"
                  onClick={() => onRowClick(p.id)}
                >
                  <td data-label="Project name">{p.name}</td>
                  <td className="projects-table-types-cell" data-label="Types">
                    <ProjectTypePills types={p.project_types ?? []} />
                  </td>
                  <td data-label="Start date">{formatTaskDate(p.start_date)}</td>
                  <td data-label="Expected end">{formatTaskDate(p.expected_end_date)}</td>
                  <td data-label="Owner">{p.created_by_username ?? '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [myProjects, setMyProjects] = useState<ProjectRow[]>([]);
  const [activeProjects, setActiveProjects] = useState<ProjectRow[]>([]);
  const [onHoldProjects, setOnHoldProjects] = useState<ProjectRow[]>([]);
  const [closedProjects, setClosedProjects] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [newProjectTypes, setNewProjectTypes] = useState<string[]>([]);
  const [newEventLocation, setNewEventLocation] = useState('');
  const [newEventStart, setNewEventStart] = useState('');
  const [newEventEnd, setNewEventEnd] = useState('');
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState('');

  const isEventProject = newProjectTypes.includes('Event');

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchProjectsCategorized(user.id);
      const isOnHold = (p: ProjectRow) => p.status === 'On Hold';
      setMyProjects(data.my.filter((p) => !isOnHold(p)));
      setActiveProjects(data.active.filter((p) => !isOnHold(p)));
      setOnHoldProjects([...data.my, ...data.active].filter(isOnHold));
      setClosedProjects(data.closed);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load projects.');
      setMyProjects([]);
      setActiveProjects([]);
      setOnHoldProjects([]);
      setClosedProjects([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setNewName('');
    setNewStart('');
    setNewEnd('');
    setNewProjectTypes([]);
    setNewEventLocation('');
    setNewEventStart('');
    setNewEventEnd('');
    setAddError('');
    setAddOpen(true);
  };

  const closeAdd = () => {
    setAddOpen(false);
    setAddError('');
  };

  const submitAdd = async () => {
    if (!user?.id) return;
    const name = newName.trim();
    if (!name) {
      setAddError('Project name is required.');
      return;
    }
    setAddSubmitting(true);
    setAddError('');
    try {
      const created = await createProject({
        name,
        project_types: newProjectTypes,
        start_date: newStart.trim() || null,
        expected_end_date: newEnd.trim() || null,
        user_id: user.id,
        ...(isEventProject
          ? {
              event_location: newEventLocation.trim() || null,
              event_start_time: newEventStart.trim() || null,
              event_end_time: newEventEnd.trim() || null,
            }
          : {}),
      });
      closeAdd();
      await load();
      navigate(`/projects/${created.id}`);
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Failed to create project.');
    } finally {
      setAddSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <div>
            <PageHeader title="Tasks Board" subtitle="View and manage task boards" />
          </div>
          <div className="page-content">
            {!user ? (
              <p className="page-subtitle">Log in to view projects.</p>
            ) : (
              <>
                <div className="page-toolbar projects-page-toolbar">
                  <div className="projects-toolbar-spacer" aria-hidden="true" />
                  <button type="button" className="page-toolbar-btn" onClick={openAdd}>
                    +&nbsp; Add project
                  </button>
                </div>
                {loadError ? (
                  <p className="create-order-error" role="alert">
                    {loadError}
                  </p>
                ) : null}
                {loading ? (
                  <p className="page-subtitle">Loading projects…</p>
                ) : (
                  <>
                    <ProjectsTableSection
                      title="My Projects"
                      sectionId="my"
                      projects={myProjects}
                      onRowClick={(id) => navigate(`/projects/${id}`)}
                    />
                    <ProjectsTableSection
                      title="Active Projects"
                      sectionId="active"
                      projects={activeProjects}
                      onRowClick={(id) => navigate(`/projects/${id}`)}
                    />
                    <ProjectsTableSection
                      title="On Hold Projects"
                      sectionId="on-hold"
                      projects={onHoldProjects}
                      onRowClick={(id) => navigate(`/projects/${id}`)}
                      emptyText="No projects are on hold."
                    />
                    <ProjectsTableSection
                      title="Closed Projects"
                      sectionId="closed"
                      projects={closedProjects}
                      onRowClick={(id) => navigate(`/projects/${id}`)}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      <Modal isOpen={addOpen} onClose={closeAdd} title="Add project" wide>
        <div className="modal-body">
          <div className="form-group">
            <span className="form-label">Project types</span>
            <p className="page-subtitle" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
              Optional. Search and add one or more categories.
            </p>
            <ProjectTypePicker
              value={newProjectTypes}
              onChange={setNewProjectTypes}
              disabled={addSubmitting}
              addPlaceholder="Search type to add…"
            />
          </div>
          <div className="form-group">
            <label htmlFor="project-new-name" className="form-label">
              Project name
            </label>
            <input
              id="project-new-name"
              type="text"
              className="form-input"
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
                setAddError('');
              }}
              placeholder="e.g. Website redesign"
              disabled={addSubmitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="project-new-start" className="form-label">
              Start date
            </label>
            <input
              id="project-new-start"
              type="date"
              className="form-input"
              value={newStart}
              onChange={(e) => setNewStart(e.target.value)}
              disabled={addSubmitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="project-new-end" className="form-label">
              Expected end date
            </label>
            <input
              id="project-new-end"
              type="date"
              className="form-input"
              value={newEnd}
              onChange={(e) => setNewEnd(e.target.value)}
              disabled={addSubmitting}
            />
          </div>
          {isEventProject ? (
            <div className="projects-event-details">
              <h3 className="projects-event-details-title">Event details</h3>
              <div className="form-group">
                <label htmlFor="project-new-event-location" className="form-label">
                  Location
                </label>
                <input
                  id="project-new-event-location"
                  type="text"
                  className="form-input"
                  value={newEventLocation}
                  onChange={(e) => setNewEventLocation(e.target.value)}
                  placeholder="e.g. Austin Convention Center, Hall B"
                  disabled={addSubmitting}
                />
              </div>
              <div className="form-group">
                <label htmlFor="project-new-event-start" className="form-label">
                  Start time
                </label>
                <input
                  id="project-new-event-start"
                  type="datetime-local"
                  className="form-input"
                  value={newEventStart}
                  onChange={(e) => setNewEventStart(e.target.value)}
                  disabled={addSubmitting}
                />
              </div>
              <div className="form-group">
                <label htmlFor="project-new-event-end" className="form-label">
                  End time
                </label>
                <input
                  id="project-new-event-end"
                  type="datetime-local"
                  className="form-input"
                  value={newEventEnd}
                  onChange={(e) => setNewEventEnd(e.target.value)}
                  disabled={addSubmitting}
                />
              </div>
            </div>
          ) : null}
          {addError ? (
            <p className="create-order-error" role="alert">
              {addError}
            </p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={addSubmitting} onClick={closeAdd}>
            Cancel
          </button>
          <button type="button" className="update-button" disabled={addSubmitting} onClick={() => void submitAdd()}>
            {addSubmitting ? 'Creating…' : 'Create project'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Projects;
